import * as React from 'react';
import {
  AlertCircle,
  CircleCheck,
  Info,
  Sparkles,
  TriangleAlert,
} from '../../primitives/Icon/icons';
import { Icon } from '../../primitives/Icon/Icon';
import type { SemanticTone } from '../../../types';
import {
  root as rootCls,
  iconCell as iconCellCls,
  body as bodyCls,
  title as titleCls,
} from './Callout.css';

/**
 * Callout's slice of the shared {@link SemanticTone} vocabulary. Maps onto
 * the document-side accent palette: info→blue, success→green, warn→amber,
 * tip→purple (philosophy / aside), danger→red. (Re-keyed from the old raw
 * palette names `blue`/`green`/`amber`/`purple`/`red` so Callout speaks the
 * same semantic language as Badge / StatusTag / Toast.)
 */
export type CalloutTone = Extract<SemanticTone, 'info' | 'success' | 'warn' | 'tip' | 'danger'>;

export interface CalloutProps extends React.HTMLAttributes<HTMLDivElement> {
  tone?: CalloutTone;
  /** Optional bold title above the body content. */
  title?: string;
  /** Override the auto-resolved tone icon. Pass `null` to omit the icon column entirely. */
  icon?: React.ReactNode | null;
  ref?: React.Ref<HTMLDivElement>;
}

const DEFAULT_ICON: Record<CalloutTone, typeof Info> = {
  info: Info,
  success: CircleCheck,
  warn: TriangleAlert,
  tip: Sparkles,
  danger: AlertCircle,
};

/**
 * Callout — soft 5-tone block with a 4px left-border accent and an
 * optional auto-resolved icon column. Used in companion documents
 * (PRD / Audit / NFR / Genesis DS) and in select in-app contexts.
 *
 * Tones are semantic (info / success / warn / tip / danger), mapped onto
 * the document-side accent palette (blue / green / amber / purple / red).
 * Each tone has a default icon (Info / CircleCheck / TriangleAlert /
 * Sparkles / AlertCircle); pass `icon={anyNode}` to override or
 * `icon={null}` to drop the icon column.
 *
 * Intentionally **not** a live region (L30): a Callout is static document
 * chrome — it's present on first paint and read in normal reading order,
 * so it carries no `role` / `aria-live`. For content that appears
 * dynamically and must be announced, use `Toast` / `Banner` (polite or
 * assertive) instead.
 */
export function Callout({
  tone = 'info',
  title,
  icon,
  children,
  ref,
  className,
  ...rest
}: CalloutProps) {
  const cls = [rootCls({ tone }), className].filter(Boolean).join(' ');
  const resolvedIcon = icon === null ? null : (icon ?? <Icon as={DEFAULT_ICON[tone]} size={16} />);
  return (
    <div ref={ref} className={cls} {...rest}>
      {resolvedIcon !== null && <span className={iconCellCls}>{resolvedIcon}</span>}
      <div className={bodyCls}>
        {title && <strong className={titleCls}>{title}</strong>}
        {children}
      </div>
    </div>
  );
}
Callout.displayName = 'Callout';
